import React, { useState, useEffect, useRef } from "react";
import {
  Avatar,
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  IconButton,
  Grid,
  List,
  ListItem,
  Divider,
} from "@mui/material";
import { Fab, useMediaQuery, useTheme } from "@mui/material";
import CookingIcon from "@mui/icons-material/LocalDining";
import CloseIcon from "@mui/icons-material/Close";
import CircularProgress from "@mui/material/CircularProgress";
import "./RecipeDetail.css"; // Import the CSS file for styling
import { useParams, useNavigate } from "react-router-dom";
import { TextField } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import CookingInterface from "./CookingInterface";
import useRecipe from "../hooks/useRecipe";

const RecipeDetail = ({ scrollToLastPosition }) => {
  const { id, action, step: stepParam } = useParams();
  const step = parseInt(stepParam, 10) || 0;

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlServings = searchParams.get("servings");
  const { recipe, currentServings, setCurrentServings } = useRecipe(id);
  const initialLoad = useRef(true); // Tracks if it's the initial load

  useEffect(() => {
    if (!recipe) return;

    // On initial load, decide whether to use URL servings or recipe's default servings
    if (initialLoad.current) {
      const urlServingsNum = parseInt(urlServings, 10);
      if (!isNaN(urlServingsNum) && urlServingsNum !== recipe.servings) {
        console.log("Setting current servings from URL:", urlServingsNum);
        setCurrentServings(urlServingsNum);
      } else {
        console.log("Setting current servings from recipe:", recipe.servings);
        setCurrentServings(recipe.servings);
      }
      initialLoad.current = false; // Mark initial load as complete
    } else {
      // For subsequent loads, update the URL if currentServings has been manually changed
      const currentServingsNum = parseInt(currentServings, 10);
      if (
        !isNaN(currentServingsNum) &&
        currentServingsNum !== parseInt(urlServings, 10)
      ) {
        console.log(
          "Updating URL servings to match currentServings:",
          currentServingsNum
        );
        searchParams.set("servings", currentServingsNum.toString());
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [recipe, currentServings, urlServings]);

  const [servingsInput, setServingsInput] = useState("");

  useEffect(() => {
    if (currentServings !== null) {
      setServingsInput(currentServings.toString());
    }
  }, [currentServings]);

  const handleServingsChange = (event) => {
    const value = event.target.value;
    setServingsInput(value); // Update input state directly

    const newServings = parseInt(value, 10);
    if (!isNaN(newServings) && newServings > 0) {
      setCurrentServings(newServings);
    } else if (value === "") {
      setCurrentServings(recipe.servings); // Reset to default if input is cleared
    }
  };

  const handleBeginCooking = () => {
    navigate(`/recipe/${id}/cooking/${step}?servings=${currentServings}`);
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  const onClose = () => {
    navigate("/");
    scrollToLastPosition();
  };

  if (!recipe) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress size={175} thickness={2} />
      </div>
    );
  }

  if (action === "cooking") {
    return <CookingInterface recipe={recipe} step={step || 0} />;
  }

  // Rest of your render logic...
  return (
    <Card
      className="recipe-detail-card"
      sx={{
        minWidth: 275,
        width: isDesktop ? "90vw" : "auto", // 90% of the viewport width on desktop
        margin: isDesktop ? "auto" : "16px", // Center the card on desktop, add margin on mobile
        marginBottom: isMobile ? "80px" : "16px", // Additional bottom margin on mobile
        height: isMobile ? "calc(100vh - 130px" : "calc(100vh - 117px)", // Auto height on mobile, fixed height on desktop
      }}
    >
      <CardContent>
        <Grid
          container
          alignItems="center"
          spacing={2}
          justifyContent="space-between"
        >
          <Grid item>
            <Avatar
              src={recipe.image || "/img/default.png"}
              alt={recipe.name}
              sx={{
                width: isDesktop ? 120 : 80,
                height: isDesktop ? 120 : 80,
                borderRadius: "10%",
              }}
            />
          </Grid>

          <Grid item xs style={{ textAlign: "left" }}>
            <Typography
              variant="h5"
              component="h2"
              style={{ textAlign: "left" }}
            >
              {recipe.name}
            </Typography>
            <Typography sx={{ mt: 1.5 }}>{recipe.description}</Typography>
            <Typography>
              {(recipe.keywords || []).map((keyword) => keyword.label).join(", ")}
            </Typography>
          </Grid>

          <Grid item>
            <IconButton onClick={onClose} aria-label="close">
              <CloseIcon />
            </IconButton>
          </Grid>
        </Grid>
      </CardContent>

      <Divider variant="middle" />

      <CardContent sx={{ flex: "1 1 auto", overflowY: "auto" }}>
        <Grid container spacing={2} alignItems="stretch">
          <Grid item xs={12} md={5}>
            <Typography variant="h5" style={{ fontWeight: "bold" }}>
              Ingredients
            </Typography>
            <List>
              <ListItem>
                <TextField
                  label="Servings"
                  type="number"
                  size="small"
                  value={servingsInput}
                  onChange={handleServingsChange}
                  margin="normal"
                  InputProps={{
                    inputProps: {
                      min: 0, // Set minimum value to 0
                    },
                  }}
                />
              </ListItem>
              {(recipe.steps || []).map((step, stepIndex) => (
                <React.Fragment key={stepIndex}>
                  {(step.ingredients || []).map((ingredient, ingredientIndex) => (
                    <ListItem key={ingredientIndex}>
                      {ingredient.formattedAmount || ingredient.amount || ""}{" "}
                      {ingredient.unit?.name || ""}{" "}
                      {ingredient.food?.name || ingredient.name || "Unnamed ingredient"}
                      {ingredient.note && ingredient.note !== ""
                        ? ` (${ingredient.note})`
                        : ""}
                    </ListItem>
                  ))}
                </React.Fragment>
              ))}
            </List>
          </Grid>

          {/* Use Grid item to wrap the Divider on desktop */}
          {isDesktop && (
            <Grid item md={1}>
              <Divider
                orientation="vertical"
                flexItem
                sx={{ height: "100%" }}
              />
            </Grid>
          )}

          {/* Conditionally render a horizontal divider for mobile */}
          {isMobile && (
            <Grid item xs={12}>
              <Divider />
            </Grid>
          )}

          <Grid item xs={12} md={5}>
            <Typography variant="h5" style={{ fontWeight: "bold" }}>
              Steps
            </Typography>
            <List>
              {(recipe.steps || []).map((step, index) => (
                <ListItem key={index}>{step.instruction}</ListItem>
              ))}
            </List>
          </Grid>
        </Grid>
      </CardContent>

      <CardContent sx={{ flex: "0 1 auto", marginTop: "auto" }}>
        {isMobile ? (
          <Fab
            color="primary"
            aria-label="begin-cooking"
            onClick={handleBeginCooking}
            sx={{ position: "fixed", bottom: 16, right: 16 }}
          >
            <CookingIcon />
          </Fab>
        ) : (
          <Button
            onClick={handleBeginCooking}
            variant="contained"
            color="primary"
            sx={{ maxWidth: "200px", margin: "auto" }}
          >
            Begin Cooking
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default RecipeDetail;
